import { type SUREbusinessCommonDomainIncassocode as Incasso } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainIncassocode';
import { type SUREbusinessCommonDomainRechtsvormEnum as Rechtsvorm } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainRechtsvormEnum';
import { SUREbusinessCommonDomainRelatiesSoortRelatieEnum as Relatietype } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainRelatiesSoortRelatieEnum';
import { SUREbusinessCommonDomainRelatiesTitulatuurcodeType as Titulatuurcode } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainRelatiesTitulatuurcodeType';
import { type InputOption } from '@shared/form/types';

export { Relatietype };

type RelatieBase = {
  relatieId: string;
  adres?: {
    huisnummer?: number | null;
    huisnummertoevoeging?: string | null;
    plaats?: string | null;
    postcode?: string | null;
    straat?: string | null;
  };
  automatischeIncassoAkkoord: boolean;
  email?: string | null;
  rekening?: Rekening;
  incasso?: Incasso;
  relatienummer: number;
  telefoonnummer?: string | null;
  adviseurnummer?: number | null;
  financieelEmailadres?: string | null;
};
export type Relatie = ZakelijkeRelatie | ParticuliereRelatie;

export type ZakelijkeRelatie = RelatieBase & {
  typeName: 'Z';
  rechtsvorm?: Rechtsvorm;
  kvknummer?: string | null;
  vestigingsnummer?: number | null;
  bedrijfsnaam?: string | null;
  sbicode?: string | null;
  contactpersoon?: Contactpersoon | null | undefined;
  website?: string | null;
};

export type ParticuliereRelatie = RelatieBase & {
  typeName: 'P';
  geboortedatum?: Date;
  voorletters?: string;
  tussenvoegsels?: string;
  achternaam?: string;
  titulatuurcode?: Titulatuurcode | null;
};

export type Contactpersoon = {
  email?: string | null;
  geboortedatum?: Date | null;
  id?: string | undefined;
  naam?: string | undefined;
  telefoonnummer?: string | null | undefined;
  titulatuurcode?: Titulatuurcode | null;
  voorletters?: string | null;
  voorvoegsels?: string | null;
  voornaam?: string | null;
};

export type Rekening = {
  id: string;
  iban: string;
  bic?: string | null;
};

export function isZakelijkeRelatie(relatie: Relatie): relatie is ZakelijkeRelatie {
  return relatie.typeName === Relatietype.Z;
}

export function isParticuliereRelatie(relatie: Relatie): relatie is ParticuliereRelatie {
  return relatie.typeName === Relatietype.P;
}

type Titulatuurcodes = (typeof Titulatuurcode)[keyof typeof Titulatuurcode];

const titulatuurcodeOptions: InputOption<Titulatuurcodes>[] = [
  {
    label: 'De heer',
    value: Titulatuurcode.Heer,
  },
  {
    label: 'Mevrouw',
    value: Titulatuurcode.Mevrouw,
  },
  {
    label: 'Heer/Mevrouw',
    value: Titulatuurcode.HeerMevrouw,
    disabled: true,
  },
];

/**
 * Geeft de beschikbare titulatuuropties terug.
 *
 * "De heer" en "Mevrouw" zijn altijd beschikbaar.
 * "Heer/Mevrouw" is een legacy-optie die langzaam wordt uitgefaseerd en
 * wordt alleen weergegeven als deze de geselecteerde waarde is.
 *
 * @param {Titulatuurcodes} selectedValue - De huidige geselecteerde waarde.
 * @returns {InputOption<Titulatuurcodes>[]} Een lijst met beschikbare titulatuuropties.
 */
export function getTitulatuurOptions(
  selectedValue?: Titulatuurcodes | null,
): InputOption<Titulatuurcodes>[] {
  return titulatuurcodeOptions.filter(
    (option) =>
      option.value !== Titulatuurcode.HeerMevrouw || selectedValue === Titulatuurcode.HeerMevrouw,
  );
}
